import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Slider,
  IconButton,
  Divider,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { getTariffs, updateTariff, createTariffRule, createCommunityTariff } from 'api';

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const CommunityPricingDialog = ({
  open,
  onClose,
  venueSlug,
  group,
  onSuccess,
}) => {
  const [tariffs, setTariffs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isVatInclusive, setIsVatInclusive] = useState(false);
  const [showNewRuleForm, setShowNewRuleForm] = useState({});
  const [newRules, setNewRules] = useState({});
  const [editingPrices, setEditingPrices] = useState({});
  
  const VAT_RATE = 20; // Default UK VAT rate
  
  // Initialize new rule template
  const getNewRuleTemplate = () => ({
    days_of_week: [],
    occupancy_min: 0,
    occupancy_max: 1,
    unit_price: 0,
    status: "AC",
    occupancy_in_days: 1,
    disabled_if_not_week: false,
    disabled_if_not_month: false,
    occupancy_instant_booking: "0.00",
    space_limit: 0,
    days_of_month: []
  });

  // Helper functions
  const safeDaysOfWeek = (daysArray) => Array.isArray(daysArray) ? daysArray : [];
  const isDayIncluded = (daysArray, day) => safeDaysOfWeek(daysArray).includes(day);

  const getDisplayPrice = (unitPrice) => {
    if (unitPrice === '' || unitPrice === null || unitPrice === undefined) return '';
    let price = parseFloat(unitPrice);
    if (isNaN(price)) return '0.00';
    if (isVatInclusive) price *= (1 + (VAT_RATE / 100));
    return price.toFixed(2);
  };

  const getVatExclusivePrice = (displayPrice) => {
    if (displayPrice === '' || displayPrice === null || displayPrice === undefined) return '0.00';
    let price = parseFloat(displayPrice);
    if (isNaN(price)) return '0.00';
    if (isVatInclusive) price = price / (1 + (VAT_RATE / 100));
    return price.toFixed(2);
  };

  // Fetch tariffs for this community
  const fetchTariffs = useCallback(async () => {
    if (!venueSlug || !group?.slug) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await getTariffs({
        venue: venueSlug,
        group: group.slug,
        booking_type: 'HD'
      });
      
      if (response?.tariffs && response.tariffs.length > 0) {
        const processedTariffs = response.tariffs.map(tariff => ({
          ...tariff,
          rules: Array.isArray(tariff.rules) ? tariff.rules.map(rule => ({
            ...rule,
            days_of_week: safeDaysOfWeek(rule.days_of_week),
            days_of_month: Array.isArray(rule.days_of_month) ? rule.days_of_month : []
          })) : []
        }));
        setTariffs(processedTariffs);
        
        // Initialize state for each tariff
        const newRulesState = {};
        const showFormState = {};
        processedTariffs.forEach(t => {
          newRulesState[t.slug] = getNewRuleTemplate();
          showFormState[t.slug] = false;
        });
        setNewRules(newRulesState);
        setShowNewRuleForm(showFormState);
      } else {
        setTariffs([]);
      }
    } catch (err) {
      console.error('Error fetching tariffs:', err);
      setError('Failed to load pricing data');
    } finally {
      setIsLoading(false);
    }
  }, [venueSlug, group?.slug]);

  useEffect(() => {
    if (open) {
      fetchTariffs();
    }
  }, [open, fetchTariffs]);

  // Create a new tariff for this community
  const handleCreateTariff = async () => {
    setIsSaving(true);
    try {
      await createCommunityTariff(venueSlug, group.slug, {
        booking_type: 'HD',
        name: `${group.name} - Hot Desk`,
        unit_price: 10, // Default starting price
      });
      await fetchTariffs();
      if (onSuccess) onSuccess('Tariff created successfully');
    } catch (err) {
      console.error('Error creating tariff:', err);
      setError('Failed to create tariff');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle base price change
  const handleTariffPriceChange = (tariffSlug, value) => {
    setEditingPrices(prev => ({ ...prev, [tariffSlug]: value }));
  };

  const handleTariffPriceBlur = (tariffSlug) => {
    const displayPrice = editingPrices[tariffSlug];
    if (displayPrice === undefined) return;
    
    const vatExclusivePrice = getVatExclusivePrice(displayPrice);
    setTariffs(prev => prev.map(t => {
      if (t.slug === tariffSlug) {
        return { ...t, unit_price: vatExclusivePrice, slot_price: vatExclusivePrice };
      }
      return t;
    }));
    setEditingPrices(prev => {
      const { [tariffSlug]: _, ...rest } = prev;
      return rest;
    });
  };

  // Handle day toggle for rules
  const handleDayToggle = (tariffSlug, ruleIndex, day) => {
    setTariffs(prev => prev.map(t => {
      if (t.slug === tariffSlug && t.rules[ruleIndex]) {
        const currentDays = safeDaysOfWeek(t.rules[ruleIndex].days_of_week);
        const newDays = isDayIncluded(currentDays, day)
          ? currentDays.filter(d => d !== day)
          : [...currentDays, day];
        const updatedRules = [...t.rules];
        updatedRules[ruleIndex] = { ...updatedRules[ruleIndex], days_of_week: newDays };
        return { ...t, rules: updatedRules };
      }
      return t;
    }));
  };

  // Handle rule price change
  const handleRulePriceChange = (tariffSlug, ruleIndex, value) => {
    setTariffs(prev => prev.map(t => {
      if (t.slug === tariffSlug && t.rules[ruleIndex]) {
        const vatExclusivePrice = getVatExclusivePrice(value);
        const updatedRules = [...t.rules];
        updatedRules[ruleIndex] = { ...updatedRules[ruleIndex], unit_price: vatExclusivePrice };
        return { ...t, rules: updatedRules };
      }
      return t;
    }));
  };

  // Handle occupancy slider change
  const handleSliderChange = (tariffSlug, ruleIndex, newValue) => {
    setTariffs(prev => prev.map(t => {
      if (t.slug === tariffSlug && t.rules[ruleIndex]) {
        const updatedRules = [...t.rules];
        updatedRules[ruleIndex] = {
          ...updatedRules[ruleIndex],
          occupancy_min: newValue[0] / 100,
          occupancy_max: newValue[1] / 100
        };
        return { ...t, rules: updatedRules };
      }
      return t;
    }));
  };

  // New rule handlers
  const handleNewRuleDayToggle = (tariffSlug, day) => {
    setNewRules(prev => {
      const current = prev[tariffSlug]?.days_of_week || [];
      const newDays = current.includes(day)
        ? current.filter(d => d !== day)
        : [...current, day];
      return { ...prev, [tariffSlug]: { ...prev[tariffSlug], days_of_week: newDays } };
    });
  };

  const handleNewRulePriceChange = (tariffSlug, value) => {
    const vatExclusivePrice = getVatExclusivePrice(value);
    setNewRules(prev => ({
      ...prev,
      [tariffSlug]: { ...prev[tariffSlug], unit_price: vatExclusivePrice }
    }));
  };

  const handleNewRuleSliderChange = (tariffSlug, newValue) => {
    setNewRules(prev => ({
      ...prev,
      [tariffSlug]: {
        ...prev[tariffSlug],
        occupancy_min: newValue[0] / 100,
        occupancy_max: newValue[1] / 100
      }
    }));
  };

  const toggleNewRuleForm = (tariffSlug) => {
    setShowNewRuleForm(prev => ({ ...prev, [tariffSlug]: !prev[tariffSlug] }));
  };

  // Add new rule
  const addTariffRule = async (tariffSlug) => {
    const tariff = tariffs.find(t => t.slug === tariffSlug);
    if (!tariff) return;
    
    const newRule = newRules[tariffSlug];
    if (!newRule) return;
    
    setIsSaving(true);
    try {
      await createTariffRule({
        ...newRule,
        tariff: tariff.slug, // Use slug instead of id for security
        status: 'AC'
      });
      await fetchTariffs();
      setNewRules(prev => ({ ...prev, [tariffSlug]: getNewRuleTemplate() }));
      setShowNewRuleForm(prev => ({ ...prev, [tariffSlug]: false }));
      if (onSuccess) onSuccess('Rule added successfully');
    } catch (err) {
      console.error('Error adding rule:', err);
      setError('Failed to add rule');
    } finally {
      setIsSaving(false);
    }
  };

  // Save tariff
  const handleSaveTariff = async (tariffSlug) => {
    const tariff = tariffs.find(t => t.slug === tariffSlug);
    if (!tariff) return;
    
    setIsSaving(true);
    try {
      await updateTariff({
        slug: tariff.slug,
        tariff: {
          name: tariff.name,
          status: tariff.status,
          start: tariff.start,
          end: tariff.end,
          unit_price: tariff.unit_price,
          slot_price: tariff.slot_price || tariff.unit_price,
          booking_type: 'HD',
          venue: tariff.venue,
          client: tariff.client,
          group: tariff.group
        },
        rules: (tariff.rules || []).map(rule => ({
          ...rule,
          tariff: tariff.id,
          days_of_week: safeDaysOfWeek(rule.days_of_week),
          days_of_month: Array.isArray(rule.days_of_month) ? rule.days_of_month : []
        }))
      });
      if (onSuccess) onSuccess('Pricing saved successfully');
    } catch (err) {
      console.error('Error saving tariff:', err);
      setError('Failed to save pricing');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Community Pricing: {group?.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* VAT Toggle */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isVatInclusive}
                onChange={(e) => setIsVatInclusive(e.target.checked)}
              />
            }
            label={`Show prices with VAT (${VAT_RATE}%)`}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : tariffs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No pricing configured for this community yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTariff}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Create Hot Desk Tariff'}
            </Button>
          </Box>
        ) : (
          tariffs.map((tariff) => (
            <Card key={tariff.slug} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">{tariff.name}</Typography>
                  <Chip
                    label={tariff.status === 'AC' ? 'Active' : 'Pending'}
                    color={tariff.status === 'AC' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>

                {/* Base Price */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Base Price (per hour)</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={editingPrices[tariff.slug] !== undefined 
                      ? editingPrices[tariff.slug] 
                      : getDisplayPrice(tariff.unit_price)}
                    onChange={(e) => handleTariffPriceChange(tariff.slug, e.target.value)}
                    onBlur={() => handleTariffPriceBlur(tariff.slug)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">£</InputAdornment>,
                    }}
                    sx={{ width: 150 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Pricing Rules */}
                <Typography variant="subtitle2" gutterBottom>
                  Pricing Rules (Day of Week / Occupancy)
                </Typography>
                
                {(tariff.rules || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No pricing rules defined. Base price applies to all days.
                  </Typography>
                )}

                {(tariff.rules || []).map((rule, ruleIndex) => (
                  <Box key={rule.slug || ruleIndex} sx={{ 
                    p: 2, 
                    mb: 1, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" display="block" gutterBottom>Days</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {daysOfWeek.map((day, dayIndex) => (
                            <Chip
                              key={day}
                              label={day}
                              size="small"
                              onClick={() => handleDayToggle(tariff.slug, ruleIndex, dayIndex)}
                              color={isDayIncluded(rule.days_of_week, dayIndex) ? 'primary' : 'default'}
                              variant={isDayIncluded(rule.days_of_week, dayIndex) ? 'filled' : 'outlined'}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="caption" display="block" gutterBottom>Price</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={getDisplayPrice(rule.unit_price)}
                          onChange={(e) => handleRulePriceChange(tariff.slug, ruleIndex, e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                          }}
                          sx={{ width: 120 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" display="block" gutterBottom>
                          Occupancy: {Math.round((rule.occupancy_min || 0) * 100)}% - {Math.round((rule.occupancy_max || 1) * 100)}%
                        </Typography>
                        <Slider
                          value={[
                            Math.round((rule.occupancy_min || 0) * 100),
                            Math.round((rule.occupancy_max || 1) * 100)
                          ]}
                          onChange={(e, val) => handleSliderChange(tariff.slug, ruleIndex, val)}
                          valueLabelDisplay="auto"
                          min={0}
                          max={100}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                {/* Add New Rule Form */}
                {showNewRuleForm[tariff.slug] && (
                  <Box sx={{ 
                    p: 2, 
                    mb: 1, 
                    bgcolor: 'primary.50', 
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'primary.main'
                  }}>
                    <Typography variant="subtitle2" gutterBottom>New Rule</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" display="block" gutterBottom>Days</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {daysOfWeek.map((day, dayIndex) => (
                            <Chip
                              key={day}
                              label={day}
                              size="small"
                              onClick={() => handleNewRuleDayToggle(tariff.slug, dayIndex)}
                              color={isDayIncluded(newRules[tariff.slug]?.days_of_week, dayIndex) ? 'primary' : 'default'}
                              variant={isDayIncluded(newRules[tariff.slug]?.days_of_week, dayIndex) ? 'filled' : 'outlined'}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="caption" display="block" gutterBottom>Price</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={getDisplayPrice(newRules[tariff.slug]?.unit_price || 0)}
                          onChange={(e) => handleNewRulePriceChange(tariff.slug, e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                          }}
                          sx={{ width: 120 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" display="block" gutterBottom>
                          Occupancy: {Math.round((newRules[tariff.slug]?.occupancy_min || 0) * 100)}% - {Math.round((newRules[tariff.slug]?.occupancy_max || 1) * 100)}%
                        </Typography>
                        <Slider
                          value={[
                            Math.round((newRules[tariff.slug]?.occupancy_min || 0) * 100),
                            Math.round((newRules[tariff.slug]?.occupancy_max || 1) * 100)
                          ]}
                          onChange={(e, val) => handleNewRuleSliderChange(tariff.slug, val)}
                          valueLabelDisplay="auto"
                          min={0}
                          max={100}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => addTariffRule(tariff.slug)}
                            disabled={isSaving}
                          >
                            Add Rule
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => toggleNewRuleForm(tariff.slug)}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  {!showNewRuleForm[tariff.slug] && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => toggleNewRuleForm(tariff.slug)}
                    >
                      Add Rule
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={() => handleSaveTariff(tariff.slug)}
                    disabled={isSaving}
                  >
                    Save Pricing
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommunityPricingDialog;
